import { useUserMutations } from '@scoring/api';
import z from 'zod';
import { iocCountryCodeSchemaNullish, validateSchema } from '@scoring/utils';
import { useAuthentication } from '@scoring/logic';

const UserFormSchema = z.object({
    id: z.guid(),
    firstName: z.string().nonempty(),
    lastName: z.string().nonempty(),
    iocCountryCode: iocCountryCodeSchemaNullish,
    profilePictureImageId: z.string().nullish(),
    organisation: z.string().nullish(),
});

const PasswordChangeFormSchema = z
    .object({
        password: z.string().min(8, 'Password must be at least 8 characters long'),
        passwordConfirm: z.string().nonempty(),
    })
    .superRefine((data, ctx) => {
        if (data.password !== data.passwordConfirm) {
            ctx.addIssue({ code: 'custom', message: 'Passwords do not match', path: ['password'] });
            ctx.addIssue({ code: 'custom', message: 'Passwords do not match', path: ['passwordConfirm'] });
        }
    });

export type ActiveUserForm = z.infer<typeof UserFormSchema>;

export type PasswordChangeForm = z.infer<typeof PasswordChangeFormSchema>;

export const useActiveUser = () => {
    const commands = useUserMutations();
    const auth = useAuthentication();

    const validate = (form: ActiveUserForm) => {
        return validateSchema(UserFormSchema, form as z.infer<typeof UserFormSchema>);
    };

    const validatePasswordChange = (form: PasswordChangeForm) => {
        return validateSchema(PasswordChangeFormSchema, form as z.infer<typeof PasswordChangeFormSchema>);
    };

    const changePassword = async (form: PasswordChangeForm) => {
        await auth.changePassword(form.password);
    };

    const update = async (form: ActiveUserForm) => {
        await commands.update.mutate.mutateAsync({ user: UserFormSchema.parse(form) });
        auth.refreshUser();
    };

    return {
        validate,
        validatePasswordChange,
        update,
        changePassword,
        user: auth.user,
        updateImage: commands.updateImage,
    };
};
